import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../hooks/useApi';
import type { TableMeta, QueryPlan, QueryResult, Session } from '../services/api';
import { MessageSquarePlus, ChevronLeft, ChevronRight, FileSpreadsheet, MessageCircle, Send, Copy, Download, Check, X, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import './ChatPanel.css';

interface Props {
  fileId: string;
  tables: TableMeta[];
  allFiles?: { file_id: string; file_name: string }[];
  activeSessionId?: string | null;
  onSessionChange?: (id: string | null) => void;
}

type MsgRole = 'user' | 'assistant' | 'plan' | 'result' | 'error';
interface Message { id: string; role: MsgRole; content: string; plan?: QueryPlan; result?: QueryResult; queryId?: string; stage?: string; }

export default function ChatPanel({ fileId, tables, allFiles, activeSessionId: parentSessionId, onSessionChange }: Props) {
  const api = useApi();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingQueryId, setPendingQueryId] = useState<string | null>(null);
  const [pendingStage, setPendingStage] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectText, setRejectText] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [activeSessionId, setSessionId] = useState<string | null>(parentSessionId || null);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([fileId]);
  const [toast, setToast] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const setActiveSession = (id: string | null) => { setSessionId(id); onSessionChange?.(id); };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  // Load sessions
  const loadSessions = useCallback(async () => {
    try { const r = await api.get('/sessions'); setSessions(r.data.sessions || []); } catch {}
  }, [api]);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Restore on mount
  useEffect(() => {
    if (parentSessionId && messages.length === 0) switchSession(parentSessionId);
  }, [parentSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildContext = (): string => {
    return messages.filter(m => m.role === 'user' || m.role === 'result' || m.role === 'assistant').slice(-10).map(m => {
      if (m.role === 'user') return `User: ${m.content}`;
      if (m.role === 'result' && m.result) return m.result.type === 'scalar' ? `Result: ${m.result.data}` : `Result: ${m.result.rows} rows`;
      if (m.role === 'assistant') return `Assistant: ${m.content}`;
      return '';
    }).filter(Boolean).join('\n');
  };

  const ensureSession = async () => {
    if (activeSessionId) return activeSessionId;
    try {
      const r = await api.post('/sessions', { name: `Chat ${new Date().toLocaleDateString()}`, file_ids: selectedFiles });
      setActiveSession(r.data.session_id); setSessions(p => [r.data, ...p]); return r.data.session_id;
    } catch { return ''; }
  };

  const switchSession = async (sid: string) => {
    setActiveSession(sid); setMessages([]); setPendingQueryId(null); setPendingStage(null);
    try {
      const r = await api.get(`/state/session/${sid}/queries`);
      const msgs: Message[] = [];
      for (const q of (r.data.queries || [])) {
        msgs.push({ id: `q-${q.query_id}`, role: 'user', content: q.query_text || '' });
        if (q.stage === 'complete' || q.stage === 'sample_done') {
          const raw = q.full_result || q.sample_result;
          if (raw) msgs.push({ id: `r-${q.query_id}`, role: 'result', content: '', result: typeof raw === 'string' ? JSON.parse(raw) : raw, queryId: q.query_id, stage: q.stage });
        } else if (q.stage === 'failed') {
          msgs.push({ id: `e-${q.query_id}`, role: 'error', content: typeof q.error === 'string' ? q.error : 'Failed' });
        }
      }
      setMessages(msgs);
    } catch {}
  };

  const add = (msg: Omit<Message, 'id'>) => setMessages(p => [...p, { ...msg, id: `${Date.now()}-${Math.random()}` }]);
  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(''), 3000); };

  const send = async () => {
    const q = input.trim(); if (!q || loading) return;
    setInput(''); add({ role: 'user', content: q }); setLoading(true);
    await ensureSession();
    try {
      const r = await api.post('/query/ask', { query: q, file_ids: selectedFiles, session_id: activeSessionId || undefined, context: buildContext() || undefined });
      setPendingQueryId(r.data.query_id); setPendingStage('plan_ready');
      add({ role: 'plan', content: '', plan: r.data.plan, queryId: r.data.query_id, stage: 'plan_ready' });
    } catch (e: any) { add({ role: 'error', content: e.response?.data?.detail || e.message || 'Failed' }); }
    finally { setLoading(false); }
  };

  const confirmPlan = async (qid: string) => {
    setLoading(true);
    try { const r = await api.post('/query/execute-sample', { query_id: qid }); setPendingStage('sample_done'); add({ role: 'result', content: '', result: r.data.sample_result, queryId: qid, stage: 'sample_done' }); }
    catch (e: any) { add({ role: 'error', content: e.response?.data?.detail || 'Sample failed' }); }
    finally { setLoading(false); }
  };

  const confirmFull = async (qid: string) => {
    setLoading(true);
    try { const r = await api.post('/query/execute-full', { query_id: qid }); setPendingQueryId(null); setPendingStage(null); add({ role: 'result', content: '', result: r.data.result, queryId: qid, stage: 'complete' }); }
    catch (e: any) { add({ role: 'error', content: e.response?.data?.detail || 'Execution failed' }); }
    finally { setLoading(false); }
  };

  const reject = (qid: string) => { setRejectingId(qid); setRejectText(''); };
  const submitReject = async () => {
    setPendingQueryId(null); setPendingStage(null); setRejectingId(null);
    if (rejectText.trim()) {
      add({ role: 'assistant', content: 'Regenerating with your feedback...' }); setLoading(true);
      try {
        const r = await api.post('/query/ask', { query: rejectText, file_ids: selectedFiles, session_id: activeSessionId || undefined, context: buildContext() + `\nFeedback: ${rejectText}` });
        setPendingQueryId(r.data.query_id); setPendingStage('plan_ready');
        add({ role: 'plan', content: '', plan: r.data.plan, queryId: r.data.query_id, stage: 'plan_ready' });
      } catch (e: any) { add({ role: 'error', content: e.response?.data?.detail || 'Failed' }); }
      finally { setLoading(false); }
    } else { add({ role: 'assistant', content: 'Plan rejected. Try a different question.' }); }
  };

  const exportResult = async (qid: string, fmt: string) => {
    try {
      const r = await api.post('/export/query-result', { query_id: qid, format: fmt }, { responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `result.${fmt}`; a.click(); flash(`Exported .${fmt}`);
    } catch { flash('Export failed'); }
  };

  const copyResult = (result: QueryResult) => {
    if (result.type === 'dataframe' && result.columns) {
      const rows = result.data as Record<string, unknown>[];
      navigator.clipboard.writeText(result.columns.join('\t') + '\n' + rows.map(r => result.columns!.map(c => String(r[c] ?? '')).join('\t')).join('\n'));
    } else { navigator.clipboard.writeText(String(result.data)); }
    flash('Copied');
  };

  const toggleFile = (fid: string) => setSelectedFiles(p => p.includes(fid) ? (p.length > 1 ? p.filter(x => x !== fid) : p) : [...p, fid]);

  const suggestions = React.useMemo(() => {
    if (!tables.length) return DEFAULTS;
    const t = tables[0]; const nums = t.columns?.filter(c => c.data_type === 'numeric').map(c => c.column_name) || [];
    const texts = t.columns?.filter(c => c.data_type === 'text').map(c => c.column_name) || [];
    const s: string[] = [];
    if (nums[0]) s.push(`What is the total ${nums[0]}?`);
    if (nums[0] && texts[0]) s.push(`Break down ${nums[0]} by ${texts[0]}`);
    s.push('How many rows are in each table?');
    if (tables.length > 1) s.push('What columns do the tables have in common?');
    return s.length ? s : DEFAULTS;
  }, [tables]);

  useEffect(() => { if (inputRef.current) { inputRef.current.style.height = 'auto'; inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 150) + 'px'; } }, [input]);

  return (
    <div className="chat-root">
      {/* ── Sidebar ── */}
      <aside className={`chat-side ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="chat-side-inner">
          {/* New chat */}
          <button className="side-new" onClick={() => { setActiveSession(null); setMessages([]); setPendingQueryId(null); }}>
            <MessageSquarePlus size={16} /> New chat
          </button>

          {/* Files */}
          {allFiles && allFiles.length > 0 && (
            <div className="side-section">
              <div className="side-label">
                <FileSpreadsheet size={12} /> Files
              </div>
              <div className="side-files">
                {allFiles.map(f => (
                  <button key={f.file_id} className={`side-file ${selectedFiles.includes(f.file_id) ? 'active' : ''}`} onClick={() => toggleFile(f.file_id)}>
                    <span className="side-file-check">{selectedFiles.includes(f.file_id) ? <Check size={12} /> : null}</span>
                    <span className="side-file-name">{f.file_name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* History */}
          <div className="side-section side-section-grow">
            <div className="side-label">
              <MessageCircle size={12} /> Recent chats
            </div>
            <div className="side-sessions">
              {sessions.length === 0 && <p className="side-empty">No chats yet</p>}
              {sessions.map(s => (
                <button key={s.session_id} className={`side-session ${activeSessionId === s.session_id ? 'active' : ''}`} onClick={() => switchSession(s.session_id)}>
                  <span className="side-session-name">{s.name}</span>
                  <span className="side-session-date">{new Date(s.created_at * 1000).toLocaleDateString()}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <button className="side-toggle" onClick={() => setSidebarOpen(o => !o)}>
          {sidebarOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </button>
      </aside>

      {/* ── Main ── */}
      <main className="chat-main">
        <div className="chat-scroll">
          {messages.length === 0 ? (
            <div className="chat-welcome">
              <div className="welcome-icon">💬</div>
              <h2 className="welcome-title">What would you like to know?</h2>
              <p className="welcome-sub">Ask questions about your Excel data in plain English</p>
              <div className="welcome-chips">
                {suggestions.map(s => (
                  <button key={s} className="welcome-chip" onClick={() => setInput(s)}>{s}</button>
                ))}
              </div>
            </div>
          ) : (
            <div className="chat-thread">
              {messages.map(msg => (
                <div key={msg.id} className={`msg ${msg.role === 'user' ? 'msg-user' : 'msg-ai'}`}>
                  <div className="msg-avatar">{msg.role === 'user' ? 'Y' : 'AI'}</div>
                  <div className="msg-body">
                    {msg.role === 'user' && <div className="msg-text">{msg.content}</div>}
                    {msg.role === 'assistant' && <div className="msg-text">{msg.content}</div>}
                    {msg.role === 'error' && <div className="msg-error">⚠ {msg.content}</div>}
                    {msg.role === 'plan' && msg.plan && (
                      <PlanBlock plan={msg.plan} qid={msg.queryId!} onConfirm={confirmPlan} onReject={() => reject(msg.queryId!)}
                        disabled={loading || pendingQueryId !== msg.queryId} confirmed={pendingQueryId !== msg.queryId || pendingStage !== 'plan_ready'} />
                    )}
                    {msg.role === 'result' && msg.result && (
                      <ResultBlock result={msg.result} qid={msg.queryId!} stage={msg.stage!}
                        onFull={confirmFull} onExport={exportResult} onCopy={copyResult}
                        disabled={loading || pendingQueryId !== msg.queryId} confirmed={msg.stage === 'complete'} />
                    )}
                  </div>
                </div>
              ))}

              {rejectingId && (
                <div className="msg msg-ai">
                  <div className="msg-avatar">AI</div>
                  <div className="msg-body">
                    <p className="msg-text" style={{ marginBottom: 8 }}>What should I change about the plan?</p>
                    <textarea className="reject-input" value={rejectText} onChange={e => setRejectText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitReject(); } }}
                      placeholder="e.g. Use a different column..." autoFocus rows={2} />
                    <div className="reject-btns">
                      <button className="btn btn-primary btn-sm" onClick={submitReject}><RotateCcw size={13} /> {rejectText.trim() ? 'Regenerate' : 'Dismiss'}</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setRejectingId(null); add({ role: 'assistant', content: 'Plan rejected.' }); setPendingQueryId(null); setPendingStage(null); }}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              {loading && (
                <div className="msg msg-ai">
                  <div className="msg-avatar">AI</div>
                  <div className="msg-body"><div className="msg-thinking"><span /><span /><span /></div></div>
                </div>
              )}
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="chat-input-area">
          <div className="chat-input-box">
            <textarea ref={inputRef} className="chat-textarea" value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about your data..." disabled={loading} rows={1} />
            <button className="chat-send-btn" onClick={send} disabled={loading || !input.trim()}>
              <Send size={16} />
            </button>
          </div>
        </div>
      </main>

      {toast && <div className="chat-toast">{toast}</div>}
    </div>
  );
}

function PlanBlock({ plan, qid, onConfirm, onReject, disabled, confirmed }: { plan: QueryPlan; qid: string; onConfirm: (id: string) => void; onReject: () => void; disabled: boolean; confirmed: boolean; }) {
  return (
    <div className="plan-block">
      <div className="plan-head"><span>📋</span> Execution Plan <span className="badge badge-accent">{plan.expected_output_format}</span></div>
      {plan.visualization && <div className="plan-viz">{plan.visualization}</div>}
      <div className="plan-steps">
        {plan.operations?.map((op, i) => (
          <div key={i} className="plan-step">
            <span className={`plan-badge op-${op.operation_type}`}>{op.operation_type}</span>
            <span className="plan-desc">{op.description}</span>
          </div>
        ))}
      </div>
      {!confirmed ? (
        <div className="plan-btns">
          <button className="btn btn-primary btn-sm" onClick={() => onConfirm(qid)} disabled={disabled}><Check size={13} /> Run on sample</button>
          <button className="btn btn-ghost btn-sm" onClick={onReject} disabled={disabled}><X size={13} /> Reject</button>
        </div>
      ) : <p className="plan-done"><Check size={13} /> Plan confirmed</p>}
    </div>
  );
}

function ResultBlock({ result, qid, stage, onFull, onExport, onCopy, disabled, confirmed }: { result: QueryResult; qid: string; stage: string; onFull: (id: string) => void; onExport: (qid: string, fmt: string) => void; onCopy: (r: QueryResult) => void; disabled: boolean; confirmed: boolean; }) {
  const [expOpen, setExpOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isDF = result.type === 'dataframe';
  const rows = isDF ? (result.data as Record<string, unknown>[]) : [];
  const cols = result.columns || [];
  const isScalar = result.type === 'scalar';
  const scalarText = isScalar ? String(result.data) : '';

  // Detect if scalar text is long (likely a sentence/paragraph answer)
  const isLongText = isScalar && scalarText.length > 100;

  // Try to format scalar text nicely — convert comma-separated lists to bullet points
  const formatScalarText = (text: string): string => {
    // If it looks like a comma-separated list of column names or items
    const items = text.split(/,\s*/);
    if (items.length > 5 && items.every(i => i.length < 80)) {
      return items.map(i => `- ${i.trim()}`).join('\n');
    }
    // If it has colons (like "Table 1: X, Table 2: Y"), format as list
    if (text.includes(':') && text.split(',').length > 3) {
      return text.split(/,\s*/).map(i => `- ${i.trim()}`).join('\n');
    }
    return text;
  };

  return (
    <div className="result-block">
      <div className="result-head">
        <span>{stage === 'complete' ? '✅' : '🔬'} {stage === 'complete' ? 'Full Result' : 'Sample (5 rows)'}</span>
        {isDF && <span className="badge badge-green">{result.rows} rows</span>}
      </div>

      {/* Scalar / text result */}
      {isScalar && !isLongText && (
        <div className="result-value-card">
          <div className="result-value">{scalarText}</div>
        </div>
      )}

      {isScalar && isLongText && (
        <div className="result-text-block">
          <div className={`result-text-content ${expanded ? 'expanded' : ''}`}>
            <ReactMarkdown>{formatScalarText(scalarText)}</ReactMarkdown>
          </div>
          {scalarText.length > 300 && (
            <button className="result-expand-btn" onClick={() => setExpanded(o => !o)}>
              {expanded ? <><ChevronUp size={13} /> Show less</> : <><ChevronDown size={13} /> Show more</>}
            </button>
          )}
        </div>
      )}

      {/* DataFrame result */}
      {isDF && rows.length > 0 && (
        <div className="result-table-wrap">
          <table className="result-tbl">
            <thead><tr>{cols.map(c => <th key={c}>{c}</th>)}</tr></thead>
            <tbody>{rows.slice(0, expanded ? 50 : 20).map((row, i) => <tr key={i}>{cols.map(c => <td key={c}>{String(row[c] ?? '')}</td>)}</tr>)}</tbody>
          </table>
          {result.rows && result.rows > rows.length && (
            <div className="result-truncated-bar">
              Showing {Math.min(rows.length, expanded ? 50 : 20)} of {result.rows} total rows.
              {' '}<button className="result-download-link" onClick={() => onExport(qid, 'xlsx')}>Download full result</button>
            </div>
          )}
          {rows.length > 20 && rows.length <= (result.rows || 0) && (
            <button className="result-expand-btn" onClick={() => setExpanded(o => !o)} style={{ margin: '8px 12px' }}>
              {expanded ? <><ChevronUp size={13} /> Show less</> : <><ChevronDown size={13} /> Show more rows</>}
            </button>
          )}
        </div>
      )}

      <div className="result-btns">
        {stage === 'sample_done' && !confirmed && <button className="btn btn-primary btn-sm" onClick={() => onFull(qid)} disabled={disabled}>▶ Run on full dataset</button>}
        <button className="btn btn-ghost btn-sm" onClick={() => onCopy(result)}><Copy size={13} /> Copy</button>
        <div style={{ position: 'relative' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setExpOpen(o => !o)}><Download size={13} /> Export</button>
          {expOpen && <div className="exp-menu">{['xlsx','csv','json'].map(f => <button key={f} onClick={() => { onExport(qid, f); setExpOpen(false); }}>.{f}</button>)}</div>}
        </div>
      </div>
    </div>
  );
}

const DEFAULTS = ['What are the column names?', 'Show me the top 10 rows', 'Total of numeric columns', 'Find duplicate rows'];
